<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ConversationSummary from './ConversationSummary.vue';
import policy from 'dashboard/components/policy.vue';

import ContactInfo from './contact/ContactInfo.vue';
import ContactOrders from './ContactOrders.vue';
import ConversationInfo from './ConversationInfo.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import Draggable from 'vuedraggable';
import MacrosList from './Macros/List.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
  onToggle: {
    type: Function,
    default: () => {},
  },
  showContactInfo: {
    type: Boolean,
    default: false,
  },
});

const {
  updateUISettings,
  isContactSidebarItemOpen,
  conversationSidebarItemsOrder,
  toggleSidebarUIState,
} = useUISettings();

const dragging = ref(false);
const conversationSidebarItems = ref([]);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => props.conversationId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);

const channelType = computed(() => currentChat.value.meta?.channel);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

watch(conversationId, (newConversationId, prevConversationId) => {
  if (newConversationId && newConversationId !== prevConversationId) {
    getContactDetails();
  }
});

watch(contactId, getContactDetails);

const onPanelToggle = props.onToggle;

const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

const SIDEBAR_CONFIG = {
  conversation_actions: true,
  conversation_summary: true,
  macros: true, 
  contact_orders: true,
  conversation_participants: true,
  conversation_info: true,
  contact_attributes: true,
  previous_conversation: true,
};

const filteredSidebarItems = computed(() => {
  return conversationSidebarItems.value.filter(item => 
    SIDEBAR_CONFIG[item.name] !== false
  );
});

const SECTION_CARD_CLASS =
  'group rounded-2xl border border-n-weak bg-n-solid-2 shadow-sm transition-colors duration-200 overflow-hidden';
const SECTION_CARD_HOVER_CLASS =
  'hover:bg-n-solid-3 hover:border-n-slate-6 hover:shadow-md';
const sectionWrapperClass = `${SECTION_CARD_CLASS} ${SECTION_CARD_HOVER_CLASS}`;

const contactInfoCardClass =
  'rounded-2xl bg-n-solid-2 shadow-sm overflow-hidden';

const sectionEmoji = {
  conversation_actions: '⚡',
  conversation_participants: '👥',
  conversation_info: 'ℹ️',
  contact_attributes: '📋',
  previous_conversation: '🕐',
  contact_orders: '🛒',
  conversation_summary: '📝',
  macros: '🎯',
};

onMounted(() => {
  conversationSidebarItems.value = conversationSidebarItemsOrder.value;
  getContactDetails();
  store.dispatch('attributes/get', 0);
});
</script>

<template>
  <div class="w-full h-fit space-y-3 p-3 xl:p-0">
    <!-- Contact Info Card -->
    <div
      v-if="showContactInfo"
      :class="contactInfoCardClass"
    >
      <ContactInfo
        :contact="contact"
        :channel-type="channelType"
        @toggle-panel="onPanelToggle"
      />
    </div>
    
    <!-- Accordion Sections -->
    <div class="flex flex-col">
      <Draggable
        :list="filteredSidebarItems"
        animation="300"
        :delay="150"
        :delay-on-touch-only="true"
        ghost-class="opacity-50"
        handle=".drag-handle"
        item-key="name"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div :key="element.name" class="group mb-2 last:mb-0">
            <!-- Conversation Actions Card -->
            <div
              v-if="element.name === 'conversation_actions'"
              :class="[sectionWrapperClass, 'overflow-y-auto']"
            >
              <AccordionItem
                :title="
                  $t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')
                "
                :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
                :emoji="sectionEmoji[element.name]"
                @toggle="
                  value => toggleSidebarUIState('is_conv_actions_open', value)
                "
              >
                <ConversationAction
                  :conversation-id="conversationId"
                  :inbox-id="inboxId"
                />
              </AccordionItem>
            </div>
            <!-- Conversation Participants Card -->
            <div
              v-else-if="element.name === 'conversation_participants'"
              :class="[sectionWrapperClass, 'relative']"
            >
              <AccordionItem
                :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
                :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
                :emoji="sectionEmoji[element.name]"
                @toggle="
                  value =>
                    toggleSidebarUIState('is_conv_participants_open', value)
                "
              >
                <ConversationParticipant
                  :conversation-id="conversationId"
                  :inbox-id="inboxId"
                />
              </AccordionItem>
            </div>
            
            <!-- Conversation Info Card -->
            <div 
              v-else-if="element.name === 'conversation_info'"
              :class="sectionWrapperClass"
            >
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
                :is-open="isContactSidebarItemOpen('is_conv_details_open')"
                compact
                :emoji="sectionEmoji[element.name]"
                @toggle="
                  value => toggleSidebarUIState('is_conv_details_open', value)
                "
              >
                <ConversationInfo
                  :conversation-attributes="conversationAdditionalAttributes"
                  :contact-attributes="contactAdditionalAttributes"
                />
              </AccordionItem>
            </div>
            
            <!-- Contact Attributes Card -->
            <div 
              v-else-if="element.name === 'contact_attributes'"
              :class="sectionWrapperClass"
            >
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
                :is-open="
                  isContactSidebarItemOpen('is_contact_attributes_open')
                "
                compact
                :emoji="sectionEmoji[element.name]"
                @toggle="
                  value =>
                    toggleSidebarUIState('is_contact_attributes_open', value)
                "
              >
                <CustomAttributes
                  attribute-type="contact_attribute"
                  attribute-from="conversation_contact_panel"
                  :contact-id="contact.id"
                  :empty-state-message="
                    $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                  "
                />
              </AccordionItem>
            </div>
            
            <!-- Previous Conversations Card -->
            <div 
              v-else-if="element.name === 'previous_conversation'"
              :class="sectionWrapperClass"
            >
              <AccordionItem
                v-if="contact.id"
                :title="
                  $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
                "
                :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
                compact
                :emoji="sectionEmoji[element.name]"
                @toggle="
                  value => toggleSidebarUIState('is_previous_conv_open', value)
                "
              >
                <ContactConversations
                  :contact-id="contact.id"
                  :conversation-id="conversationId"
                />
              </AccordionItem>
            </div>
            
            <!-- Contact Orders Card -->
            <div 
              v-else-if="element.name === 'contact_orders'"
              :class="sectionWrapperClass"
            >
              <AccordionItem
                v-if="contact.id"
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ORDERS')"
                :is-open="isContactSidebarItemOpen('is_contact_orders_open')"
                compact
                :emoji="sectionEmoji[element.name]"
                @toggle="
                  value => toggleSidebarUIState('is_contact_orders_open', value)
                "
              >
                <ContactOrders :contact-id="contact.id" />
              </AccordionItem>
            </div>
            <!-- Conversation Summary Card -->
            <div 
              v-else-if="element.name === 'conversation_summary'"
              :class="sectionWrapperClass"
            >
              <policy
                :permissions="['administrator', 'agent']"
                :feature-flag="'AI_CONVERSATION_SUMMARY'"
              >
                <AccordionItem
                  :title="
                    $t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_SUMMARY')
                  "
                  :is-open="isContactSidebarItemOpen('is_conv_summary_open')"
                  compact
                  :emoji="sectionEmoji[element.name]"
                  @toggle="
                    value => toggleSidebarUIState('is_conv_summary_open', value)
                  "
                >
                  <ConversationSummary :conversation-id="conversationId" />
                </AccordionItem>
              </policy>
            </div>
            
            <!-- Macros Card -->
            <div 
              v-else-if="element.name === 'macros'"
              :class="sectionWrapperClass"
            >
              <woot-feature-toggle feature-key="macros">
                <AccordionItem
                  :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
                  :is-open="isContactSidebarItemOpen('is_macro_open')"
                  compact
                  :emoji="sectionEmoji[element.name]"
                  @toggle="value => toggleSidebarUIState('is_macro_open', value)"
                >
                  <MacrosList :conversation-id="conversationId" />
                </AccordionItem>
              </woot-feature-toggle>
            </div>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .contact--profile {
    @apply p-4 border-b border-n-weak;
  }

  .conversation--actions .multiselect-wrap--small {
    .multiselect {
      @apply box-border rounded-md border border-n-weak bg-n-solid-2 pl-4 transition-colors duration-200;
    }

    .multiselect:hover {
      @apply border-n-slate-6 bg-n-solid-3;
    }

    .multiselect__element {
      span {
        @apply w-full;
      }
    }
  }

  .multiselect__tags {
    @apply rounded-md border-0 bg-transparent px-2 py-1;
  }

  .multiselect__single {
    @apply bg-transparent p-0 m-0 leading-normal;
  }

  .multiselect__input {
    @apply bg-transparent;
  }
}

:deep(.drag-handle) {
  @apply cursor-grab border-b border-n-weak bg-n-solid-2 transition-colors duration-200 rounded-t-2xl;

  &:hover {
    @apply bg-n-solid-3;
  }

  &:active {
    @apply cursor-grabbing;
  }
}
</style>
